import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Label, Parameters, Rank, Tooltip} from 'platform/components';
import {Box, getSize, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {DateTimeFormat, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {ReactElement} from 'react';

import {isNil, isNotNil} from 'ramda';
import {floor} from 'ramda-adjunct';

import {SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {CurrencyCodeType, EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {useVehicleDoubleClick} from '../../hooks/useVehicleDoubleClick';
import {decimalToPercentRank} from '../../utils/decimalToPercentRank';
import {detailSecondLine} from '../../utils/detailSecondLine';
import {getDetailFirstLine, getDetailFirstLineForTooltip} from '../../utils/getDetailFirstLine';
import {getLID} from '../../utils/getLID';
import {getSuspiciousReasonsLabel} from '../../utils/getSuspiciousReasonsLabel';
import {getVehicleDetailPosition} from '../../utils/getVehicleDetailPosition';
import {isClassifiedsAdInactive} from '../../utils/isClassifiedsAdInactive';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {ActionButtonsVehicle} from '../ActionButtonsVehicle/ActionButtonsVehicle';
import {AttractivenessFlag} from '../AttractivenessFlag/AttractivenessFlag';
import {Clamp} from '../Clamp/Clamp';
import {ClassifiedsSoldFlag} from '../ClassifiedsSoldFlag/ClassifiedsSoldFlag';
import {CountryFlag} from '../CountryFlag/CountryFlag';
import {HitlistBlacklistFlag} from '../HitlistBlacklistFlag/HitlistBlacklistFlag';
import {PriceChangesPopper} from '../PriceChangesPopper/PriceChangesPopper';
import {getFirstScrapedPrice} from '../PriceChangesPopper/utils/getFirstScrapedPrice';
import {Car, CarContentColumn, CarImageColumn} from '../SourcingCard/components/Car';
import {CardStatsGrid} from '../SourcingCard/components/CardStatsGrid';
import {SOURCING_CARD_GEOMETRY} from '../SourcingCard/constants/SOURCING_CARD_GEOMETRY';
import {SourcingCebiaReportBadge} from '../SourcingCebiaReport/components/SourcingCebiaReportBadge';
import {createRowElementId} from '../SourcingTable/utils/createRowElementId';
import {getAdvertisedTime} from '../SourcingTable/utils/getAdvertisedTime';
import {convertVehicleTagsToVehicleHighlights} from '../VehicleHighlights/utils/convertVehicleTagsToVehicleHighlights';
import {getInteriorMaterialHighlights} from '../VehicleHighlights/utils/getInteriorMaterialHighlights';
import {VehicleHighlights} from '../VehicleHighlights/VehicleHighlights';
import {VehicleImage} from '../VehicleImage/VehicleImage';

export interface AdvanceVehicleViewProps extends TestIdProps {
  index?: number;
  isDetail?: boolean;
  isHoverDisabled?: boolean;
  vehicle?: SourcingVehicleDetailResponseBody;
  isVehicleLoading?: boolean;
  onRowClick?: () => void;
  isLastClickedRow?: boolean;
  setLastVisitedVehicle?: (adId: string, index: number) => void;
  children?: ReactElement;
}

const PERCENTAGE_MULTIPLIER = 100;

// eslint-disable-next-line complexity -- Pre-existing violation
export function AdvanceVehicleView(props: AdvanceVehicleViewProps) {
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const onDoubleClick = useVehicleDoubleClick(props.vehicle?.sourcingVehicle.adId, () =>
    props.setLastVisitedVehicle?.(props.vehicle?.sourcingVehicle.adId ?? '', props.index ?? 0)
  );
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();

  const sourcingVehicle =
    props.vehicle && 'sourcingVehicle' in props.vehicle ? props.vehicle.sourcingVehicle : null;
  const interiorMaterialHighlights = getInteriorMaterialHighlights(
    sourcingVehicle?.interiorMaterial
  );
  const isInactive = isClassifiedsAdInactive(sourcingVehicle);
  const mutedTextColor = isInactive ? 'tertiary' : undefined;

  const vehiclePosition = getVehicleDetailPosition(
    sourcingVehicle?.extraData?.marketPosition,
    sourcingVehicle?.extraData?.marketPositionCount,
    sourcingVehicle?.pricePercentile
  );
  const originalPrice = sourcingVehicle?.price?.originalWithVat;
  const originalCurrency = sourcingVehicle?.price?.originalCurrency;
  const currency = sourcingVehicle?.price?.currency;
  const originalCurrencyCode = originalCurrency?.key as CurrencyCodeType;
  const convertedPrice = props.vehicle?.summary.original;
  const convertedCurrencyCode = currency?.key as CurrencyCodeType;
  const firstScrapedPrice = getFirstScrapedPrice(sourcingVehicle, convertedPrice);
  const suspiciousReasons = getSuspiciousReasonsLabel(sourcingVehicle?.suspiciousReason);
  const {firstOccurrenceDateTime, shouldShowTime, firstOccurrenceDistance} = getAdvertisedTime(
    sourcingVehicle,
    formatNumber
  );
  const isMarginNegative = (sourcingVehicle?.maximalPossibleMargin ?? 0) < 0;
  const marginColor = match({isInactive, isMarginNegative})
    .with({isInactive: true}, () => 'tertiary' as const)
    .with({isMarginNegative: true}, () => 'danger' as const)
    .otherwise(() => undefined);
  const dodColor = match({isInactive, shouldShowTime})
    .with({isInactive: true}, () => 'tertiary' as const)
    .with({shouldShowTime: true}, () => 'success' as const)
    .otherwise(() => 'primary' as const);

  const getActionButtons = () => {
    if (isNil(props.vehicle)) {
      return null;
    }

    return (
      <ActionButtonsVehicle
        vehicle={props.vehicle}
        onDetailOpen={() =>
          props.setLastVisitedVehicle?.(sourcingVehicle?.adId ?? '', props.index ?? 0)
        }
        data-testid={suffixTestId('actionButtonsVehicle', props)}
      />
    );
  };

  const actionButtons = getActionButtons();

  return (
    <Car
      $enhancedSurfaces={enhancedSurfaces}
      id={props.vehicle && createRowElementId(props.vehicle)}
      key={sourcingVehicle?.adId}
      $isDetail={props.isDetail}
      $isHoverDisabled={props.isHoverDisabled}
      $isActive={props.isLastClickedRow}
      $isNew={props.vehicle?.isNew}
      $isInactive={isInactive}
      onClick={props.onRowClick}
      onDoubleClick={props.isDetail ? undefined : onDoubleClick}
    >
      <CarImageColumn isDisabled={isInactive}>
        <VehicleImage
          vehicle={props.vehicle}
          borderRadius="medium"
          hasUnifiedSurfaces={enhancedSurfaces}
        />
      </CarImageColumn>
      <CarContentColumn>
        <DataStatus isLoading={props.isVehicleLoading}>
          <Box padding={3}>
            <VStack spacing={enhancedSurfaces ? 3 : 2}>
              <HStack align="center" spacing={2} wrap justify="flex-end">
                {/* The flags and the unread dot must stay inside this group: as siblings of the row
                    they get wrapped away from the title. minWidth={0} lets the title's Clamp still
                    ellipsize; when the group cannot get its basis, the action buttons wrap below it. */}
                <HStack
                  align="center"
                  spacing={2}
                  grow={1}
                  minWidth={0}
                  basis={getSize(SOURCING_CARD_GEOMETRY.titleMinWidth)}
                >
                  <CountryFlag country={sourcingVehicle?.seller?.country?.key} />
                  <ClassifiedsSoldFlag
                    sourcingVehicle={sourcingVehicle}
                    data-testid={suffixTestId('sold', props)}
                  />
                  <Show when={props.vehicle?.isNew}>
                    <Box
                      backgroundColor="palettes.green.60.100"
                      width={3}
                      height={3}
                      borderRadius="circular"
                    />
                  </Show>
                  <Tooltip label={sourcingVehicle && getDetailFirstLineForTooltip(sourcingVehicle)}>
                    <Clamp lines={1}>
                      <Heading
                        size={4}
                        color={mutedTextColor}
                        data-testid={suffixTestId('vehicleName', props)}
                      >
                        {sourcingVehicle && getDetailFirstLine(sourcingVehicle)}
                      </Heading>
                    </Clamp>
                  </Tooltip>
                  <Show when={sourcingVehicle?.adId}>
                    {(externalId) => <SourcingCebiaReportBadge externalId={externalId} />}
                  </Show>
                  <Show when={props.vehicle?.vehicleSummary?.comment}>
                    <Tooltip placement="top" label={props.vehicle?.vehicleSummary?.comment}>
                      <Icon value="communication/comment" />
                    </Tooltip>
                  </Show>
                  <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                    <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
                  </Show>
                  <Show
                    whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                    when={sourcingVehicle?.suspicious}
                  >
                    <Tooltip label={suspiciousReasons}>
                      <Icon value="alert/warning" color="severity.warning" size={5} />
                    </Tooltip>
                  </Show>
                </HStack>
                <Box flexShrink={0}>{actionButtons}</Box>
              </HStack>
              <Show when={isNotNil(sourcingVehicle)}>
                <Parameters
                  size="small"
                  color={isInactive ? 'tertiary' : 'secondary'}
                  parameters={detailSecondLine(formatNumber, sourcingVehicle)}
                  data-testid={suffixTestId('vehicleParameters', props)}
                />
              </Show>
              <VehicleHighlights
                data={convertVehicleTagsToVehicleHighlights(
                  [],
                  [],
                  Object.values(props.vehicle?.features ?? {}).filter((i) => i.isHighlight),
                  interiorMaterialHighlights
                )}
              />
              <CardStatsGrid enhancedSurfaces={enhancedSurfaces}>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.position')}</Label>
                  <Text size="xSmall" color={mutedTextColor}>
                    {vehiclePosition[0]}
                  </Text>
                  <Space vertical={1} />
                  <Text size="xxSmall" color="tertiary">
                    {vehiclePosition[1]}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.originalPrice')}</Label>
                  <HStack spacing={1} align="center">
                    <Show
                      when={
                        isNotNil(sourcingVehicle) && isNotNil(convertedPrice)
                          ? {sourcingVehicle, convertedPrice}
                          : null
                      }
                    >
                      {(price) => (
                        <PriceChangesPopper
                          vehicle={price.sourcingVehicle}
                          currentPrice={price.convertedPrice}
                          currency={convertedCurrencyCode}
                          popperPlacement="top-start"
                        />
                      )}
                    </Show>
                    <Tooltip
                      label={
                        isNotNil(originalPrice)
                          ? formatCurrency(originalPrice, originalCurrencyCode)
                          : undefined
                      }
                    >
                      <Text
                        size={enhancedSurfaces ? 'small' : 'xSmall'}
                        alternative={enhancedSurfaces}
                        color={mutedTextColor}
                      >
                        {isNotNil(convertedPrice)
                          ? formatCurrency(convertedPrice, convertedCurrencyCode)
                          : EMPTY_PLACEHOLDER}
                      </Text>
                    </Tooltip>
                  </HStack>
                  <Show
                    when={
                      isNotNil(convertedPrice) &&
                      isNotNil(firstScrapedPrice) &&
                      firstScrapedPrice !== convertedPrice
                    }
                    whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}
                  >
                    <Space vertical={1} />
                    <Text size="xxSmall" color="tertiary" strikethrough>
                      {isNotNil(firstScrapedPrice)
                        ? formatCurrency(firstScrapedPrice, convertedCurrencyCode)
                        : null}
                    </Text>
                  </Show>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.margin')}</Label>
                  <Text size="xSmall" alternative={enhancedSurfaces} color={marginColor}>
                    {isNotNil(sourcingVehicle?.maximalPossibleMargin)
                      ? formatCurrency(
                          sourcingVehicle?.maximalPossibleMargin,
                          convertedCurrencyCode
                        )
                      : EMPTY_PLACEHOLDER}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.recommendedPrice')}</Label>
                  <Text size="xSmall" alternative={enhancedSurfaces} color={mutedTextColor}>
                    {isNotNil(sourcingVehicle?.extraData?.predictedPrice)
                      ? formatCurrency(
                          sourcingVehicle?.extraData?.predictedPrice,
                          convertedCurrencyCode
                        )
                      : EMPTY_PLACEHOLDER}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.LID')}</Label>
                  <Text size="xSmall" color={mutedTextColor}>
                    {isNotNil(props.vehicle?.summary?.lid)
                      ? formatNumber(getLID(props.vehicle?.summary?.lid), 0)
                      : EMPTY_PLACEHOLDER}
                  </Text>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.DOD')}</Label>
                  <Tooltip
                    label={
                      isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT) ? (
                        <DateTimeFormat
                          value={firstOccurrenceDateTime}
                          format={shouldShowTime ? 'dateTimeShort' : 'dateShort'}
                        />
                      ) : undefined
                    }
                  >
                    <Text size="xSmall" color={dodColor} overflowWrap="anywhere">
                      {firstOccurrenceDistance}
                    </Text>
                  </Tooltip>
                </Box>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.features')}</Label>
                  <Tooltip label={decimalToPercentRank(props.vehicle?.summary?.rank ?? undefined)}>
                    <Rank
                      percentage={floor(
                        (props.vehicle?.summary?.rank ?? 0) * PERCENTAGE_MULTIPLIER
                      )}
                      data-testid={suffixTestId('rank', props)}
                    />
                  </Tooltip>
                </Box>
                <Show whenFeatureEnabled={featureFlags.SOURCING_ATTRACTIVENESS_SCORE}>
                  <Box>
                    <Label>{i18n.t('entity.vehicle.labels.attractiveness')}</Label>
                    <AttractivenessFlag sourcingVehicle={sourcingVehicle} />
                  </Box>
                </Show>
              </CardStatsGrid>
            </VStack>
          </Box>
        </DataStatus>
      </CarContentColumn>
    </Car>
  );
}
